import React, { useRef, useState } from "react";
import { Upload, Loader2, Play, Wand2, Mic, Square } from "lucide-react";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { waitTask } from "@/components/voiceKit";


const SourceBtn = ({ on, label, onClick, testid, disabled }) => (
  <button type="button" disabled={disabled} onClick={onClick} data-testid={testid}
    className={`rounded-full px-3 py-1.5 text-xs transition-colors disabled:opacity-40 ${on ? "bg-primary text-white" : "text-muted-foreground hover:text-foreground"}`}>{label}</button>
);

export const VoiceTrainer = ({ voice, onChange }) => {
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  const [ref, setRef] = useState(null);
  const trained = !!voice.clone_id;
  const rec = useRef(null);
  const [secs, setSecs] = useState(null);
  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      const chunks = [];
      mr.ondataavailable = (e) => e.data.size && chunks.push(e.data);
      mr.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        clearInterval(rec.current.timer);
        const took = rec.current.secs;
        setSecs(null);
        if (took < 3) return toast.error("التسجيل قصير — سجّل 10 ثواني على الأقل");
        const type = mr.mimeType || "audio/webm";
        upload(new File([new Blob(chunks, { type })], `تسجيل-${voice.name || "صوت"}.${type.includes("mp4") ? "m4a" : "webm"}`, { type }));
      };
      rec.current = { mr, secs: 0, timer: setInterval(() => {
        rec.current.secs += 1; setSecs(rec.current.secs);
        if (rec.current.secs >= 60) mr.stop();
      }, 1000) };
      mr.start(); setSecs(0);
    } catch { toast.error("ما قدرنا نوصل للمايكروفون — اسمح للمتصفح باستخدامه"); }
  };
  const stopRec = () => rec.current?.mr.state === "recording" && rec.current.mr.stop();
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(`/admin/voices/${voice.id}/train`, fd);
      toast.info(`جارٍ تدريب الصوت من تسجيل ${data.seconds} ثانية${data.looped ? " (كررناه تلقائياً ليكفي التدريب)" : ""}…`);
      await waitTask(data.task_id);
      const { data: all } = await api.get("/admin/voices");
      const fresh = all.voices.find((x) => x.id === voice.id) || {};
      onChange({ clone_id: fresh.clone_id, ref_id: fresh.ref_id, ref_name: fresh.ref_name, source: "clone" });
      toast.success("تم تدريب الصوت — جرّبه ثم احفظ واعتمد");
    } catch (err) { toast.error(err.message && !err.response ? err.message : formatError(err.response?.data?.detail)); }
    finally { setBusy(false); if (input.current) input.current.value = ""; }
  };
  const playRef = async () => {
    try { const { data } = await api.get(`/admin/voices/${voice.id}/reference`, { responseType: "blob" }); setRef(URL.createObjectURL(data)); }
    catch { toast.error("لا يوجد تسجيل"); }
  };
  return (
    <div className="space-y-3" data-testid="voice-trainer">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="font-bold text-sm text-primary flex items-center gap-2"><Wand2 className="w-4 h-4" /> مصدر الصوت والتدريب</div>
        <div className="inline-flex rounded-full border border-border bg-secondary p-1 gap-1">
          <SourceBtn on={voice.source !== "clone"} label="صوت أساسي" onClick={() => onChange({ source: "preset" })} testid="voice-source-preset" />
          <SourceBtn on={voice.source === "clone"} label="مستنسخ من تسجيلي" disabled={!trained} onClick={() => onChange({ source: "clone" })} testid="voice-source-clone" />
        </div>
      </div>
      {!voice.id ? <p className="text-xs text-muted-foreground">احفظ الصوت أولاً، ثم افتحه للتعديل وارفع تسجيلاً لتدريبه.</p> : (
        <>
          <p className="text-xs text-muted-foreground leading-relaxed">ارفع ملف صوتي (MP3 أو M4A أو WAV أو مذكرة صوتية من الجوال) أو سجّل مباشرة. الأفضل 15–30 ثانية. بعد التدريب اختر "مستنسخ من تسجيلي" ثم جرّب في الخطوة 4. نحفظ التسجيل ونعيد التدريب تلقائياً إذا انتهت صلاحية الصوت.</p>
          <div className="flex items-center gap-2 flex-wrap">
            <input ref={input} type="file" accept="audio/*,video/*,.m4a,.mp3,.wav,.aac,.ogg,.opus,.webm,.caf,.amr,.mp4,.mov" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="voice-train-file" />
            <Button size="sm" onClick={() => input.current?.click()} disabled={busy} className="gap-2" data-testid="voice-train-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} {busy ? "جارٍ التدريب…" : trained ? "إعادة التدريب بتسجيل جديد" : "رفع تسجيل وتدريب"}
            </Button>
            {secs === null
              ? <Button size="sm" variant="outline" onClick={startRec} disabled={busy} className="gap-2 border-border" data-testid="voice-record-btn"><Mic className="w-4 h-4 text-destructive" /> تسجيل بالمايكروفون</Button>
              : <Button size="sm" onClick={stopRec} className="gap-2 bg-destructive hover:bg-destructive/90 text-white" data-testid="voice-record-stop"><Square className="w-4 h-4" /> إيقاف وتدريب ({secs} ث{secs < 10 ? ` — باقي ${10 - secs}` : ""})</Button>}
            {voice.ref_id && <Button size="sm" variant="outline" onClick={playRef} className="gap-2 border-border" data-testid="voice-ref-play"><Play className="w-4 h-4" /> التسجيل الأصلي</Button>}
            {trained && <span className="text-xs text-accent" data-testid="voice-trained-badge">مدرّب{voice.ref_name ? ` · ${voice.ref_name}` : ""}</span>}
          </div>
          {ref && <audio src={ref} controls className="w-full h-9" data-testid="voice-ref-audio" />}
        </>
      )}
    </div>
  );
};
